import * as React from "react";
import { toast as sonnerToast } from "sonner";

type CompatibleToast = {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "destructive" | "warning";
  action?: React.ReactNode;
  [key: string]: unknown;
};

const normalizeTitle = (title: React.ReactNode): React.ReactNode => title;

const buildOptions = (props: CompatibleToast): Record<string, unknown> => ({
  ...props,
  id: props.id,
  description: props.description,
  action: props.action,
});

const showToast = (props: CompatibleToast): string | number => {
  const title = normalizeTitle(props.title ?? "");
  const options = buildOptions(props);

  if (props.variant === "destructive") {
    return sonnerToast.error(title, options);
  }

  if (props.variant === "warning") {
    return sonnerToast.warning(title, options);
  }

  return sonnerToast(title, options);
};

type SonnerTitle = React.ReactNode;
type SonnerOptions = Record<string, unknown>;

type ToastFn = {
  (title: SonnerTitle, options?: SonnerOptions): string | number;
  (props: CompatibleToast): string | number;
  success: (title: SonnerTitle, options?: SonnerOptions) => string | number;
  error: (title: SonnerTitle, options?: SonnerOptions) => string | number;
  info: (title: SonnerTitle, options?: SonnerOptions) => string | number;
  warning: (title: SonnerTitle, options?: SonnerOptions) => string | number;
  loading: (title: SonnerTitle, options?: SonnerOptions) => string | number;
  dismiss: (id?: string | number) => void;
  promise: typeof sonnerToast.promise;
};

const isCompatibleToastObject = (value: unknown): value is CompatibleToast => {
  return typeof value === "object" && value !== null;
};

const toastBase = (
  titleOrProps: SonnerTitle | CompatibleToast,
  options?: SonnerOptions
): string | number => {
  if (isCompatibleToastObject(titleOrProps)) {
    return showToast(titleOrProps);
  }

  return sonnerToast(titleOrProps, options);
};

const toast = toastBase as ToastFn;

toast.success = (title, options) => sonnerToast.success(title, options);
toast.error = (title, options) => sonnerToast.error(title, options);
toast.info = (title, options) => sonnerToast.info(title, options);
toast.warning = (title, options) => sonnerToast.warning(title, options);
toast.loading = (title, options) => sonnerToast.loading(title, options);
toast.dismiss = (id) => sonnerToast.dismiss(id);
toast.promise = (
  typeof sonnerToast.promise === "function"
    ? sonnerToast.promise.bind(sonnerToast)
    : () => Promise.resolve()
) as typeof sonnerToast.promise;

function createToastController(props: CompatibleToast): {
  id: string | number;
  dismiss: () => void;
  update: (nextProps: CompatibleToast) => void;
} {
  const id = toast(props);

  return {
    id,
    dismiss: () => toast.dismiss(id),
    update: (nextProps: CompatibleToast) => {
      toast({ ...nextProps, id: String(id) });
    },
  };
}

function useToast(): {
  toasts: CompatibleToast[];
  toast: (
    title: SonnerTitle | CompatibleToast,
    options?: SonnerOptions
  ) => ReturnType<typeof createToastController>;
  dismiss: (toastId?: string | number) => void;
} {
  return {
    toasts: [],
    toast: (titleOrProps, options) => {
      if (isCompatibleToastObject(titleOrProps)) {
        return createToastController(titleOrProps);
      }

      const id = toast(titleOrProps, options);
      return {
        id,
        dismiss: () => toast.dismiss(id),
        update: (nextProps: CompatibleToast) => {
          toast({ ...nextProps, id: String(id) });
        },
      };
    },
    dismiss: (toastId?: string | number) => toast.dismiss(toastId),
  };
}

export { useToast, toast };
